// =============================================================================
// app/src/lib/listaDeContas.ts — A LISTA DAS CONTAS A PAGAR, para o pai do dono.
//
// Pedido do dono (22/09/2026): "emitir um pdf com a lista de todas as contas a
// pagar cadastradas, para enviar para o meu pai um arquivo em .pdf ou em
// imagem que mostre para ele todas as contas para os próximos dias".
//
// PARA QUEM ESTE PAPEL É ESCRITO (o mesmo leitor do diaDoc.ts): o pai não usa
// o sistema, lê no celular — às vezes no dia seguinte — e não vai clicar em
// nada. Por isso:
//
//   * UM número grande (quanto pagar até a data), e as ressalvas em palavras:
//     "deste total, R$ X já venceu" tira a dúvida se o atrasado está dentro;
//   * data SEMPRE por extenso e absoluta ("quarta-feira, 23 de setembro"),
//     nunca "hoje" ou "amanhã" — o arquivo é lido depois de feito, e palavra
//     relativa envelhece mentindo;
//   * conta sem valor diz "a definir", é CONTADA e nunca somada (046); nunca
//     "R$ 0,00";
//   * valor estimado (valor_real nulo) diz "valor previsto", em palavra — o
//     "~" em itálico da tela não se lê num celular;
//   * a atrasada entra sempre, no topo, qualquer que seja o período;
//   * toda página diz "página X de N" e a última diz "Fim da lista": uma foto
//     que não chegou se nota;
//   * o que não aconteceu não aparece (sem bloco de atrasadas vazio);
//   * só preto e cinza.
//
// FICA DE FORA, de propósito: a categoria (há rótulo errado nos dados, e no
// papel ele induz a erro), a observação (texto livre — nem chega à memória:
// COLUNAS_DA_LISTA), os anexos, quem lançou, a forma de pagamento. Os filtros
// da tela NÃO valem aqui: lista recortada chegaria parecendo completa. A única
// exclusão é a dos salários, e aí o papel DIZ que tirou.
//
// COMO É FEITO: a paginação e todas as regras moram AQUI, puras, e devolvem
// uma lista de PEÇAS posicionadas (texto, linha, retângulo, marca) em mm com
// o Y para baixo. O PDF e a imagem só redesenham as mesmas peças
// (folhaSaida.ts) — os dois formatos não podem divergir.
// =============================================================================
import { negocio } from '@negocio';
import { brl } from './formato';
import { formatarLinha } from './codigoBarras';
import { larguraExata, representavelEmWinAnsi, type Alinhamento } from './pdf';
import { somarDias, valorDaConta, type ContaParaLista } from './contasPagar';
import { DIAS, MESES } from './diaDoc';

export type { ContaParaLista } from './contasPagar';

// --- O período --------------------------------------------------------------------

export type PeriodoDaLista = 7 | 15 | 30 | 'tudo';

export const PERIODOS: ReadonlyArray<{ id: PeriodoDaLista; rotulo: string }> = [
  { id: 7, rotulo: '7 dias' },
  { id: 15, rotulo: '15 dias' },
  { id: 30, rotulo: '30 dias' },
  { id: 'tudo', rotulo: 'Tudo em aberto' },
];

/** Salários e pró-labore: a única exclusão que o dono pode fazer. */
const CATEGORIAS_DE_SALARIO: ReadonlySet<string> = new Set(['salarios', 'pro_labore']);

export interface DiaDaLista {
  dia: string;
  contas: ContaParaLista[];
  /** Só das contas COM valor. */
  subtotal: number;
  semValor: number;
}

export interface ListaDeContas {
  hoje: string;
  periodo: PeriodoDaLista;
  /** Último dia incluído ('YYYY-MM-DD'); nulo = tudo o que está em aberto. */
  ate: string | null;
  /** Vencidas e não pagas — a mais antiga primeiro. Entram sempre. */
  atrasadas: ContaParaLista[];
  /** De hoje até `ate`, um grupo por dia, em ordem. */
  dias: DiaDaLista[];
  /** Atrasadas + do período. */
  quantidade: number;
  /** Soma das que têm valor (atrasadas incluídas). */
  total: number;
  /** Quantas estão "a definir" — contadas, nunca somadas. */
  semValor: number;
  atrasadasTotal: number;
  atrasadasSemValor: number;
  /** Quantas contas somadas são estimativa (valor_real nulo). */
  previstas: number;
  /** Quantas contas de salário/pró-labore ficaram de fora a pedido do dono. */
  salariosFora: number;
}

const ordemNoDia = (a: ContaParaLista, b: ContaParaLista) => {
  const va = valorDaConta(a);
  const vb = valorDaConta(b);
  // Do maior para o menor; sem valor por último; empate pelo nome.
  if (va === null && vb !== null) return 1;
  if (vb === null && va !== null) return -1;
  if (va !== null && vb !== null && va !== vb) return vb - va;
  return a.descricao.localeCompare(b.descricao, 'pt-BR');
};

/**
 * As contas que entram no papel, já agrupadas e somadas.
 *
 * `hoje` vem de fora (hojeISO() do aparelho, o dia da loja): a função é pura
 * e testável em qualquer fuso.
 */
export function prepararLista(
  contas: ReadonlyArray<ContaParaLista>,
  opcoes: { hoje: string; periodo: PeriodoDaLista; comSalarios: boolean },
): ListaDeContas {
  const { hoje, periodo, comSalarios } = opcoes;
  const ate = periodo === 'tudo' ? null : somarDias(hoje, periodo - 1);
  const doPeriodo = contas.filter((c) => !c.pago_em && (ate === null || c.vencimento <= ate));
  // Contados DENTRO do período: salário de outubro numa lista de 7 dias não
  // "ficou de fora" — ele não entraria de qualquer jeito, e a frase "não
  // inclui salários" faria o pai procurar um salário que não vence (revisão
  // de 22/09/2026).
  const salariosFora = comSalarios ? 0 : doPeriodo.filter((c) => CATEGORIAS_DE_SALARIO.has(c.categoria)).length;
  const entram = doPeriodo.filter((c) => comSalarios || !CATEGORIAS_DE_SALARIO.has(c.categoria));

  const atrasadas = entram
    .filter((c) => c.vencimento < hoje)
    .sort((a, b) => a.vencimento.localeCompare(b.vencimento) || ordemNoDia(a, b));

  const porDia = new Map<string, ContaParaLista[]>();
  for (const c of entram) {
    if (c.vencimento < hoje) continue;
    porDia.set(c.vencimento, [...(porDia.get(c.vencimento) ?? []), c]);
  }
  const dias: DiaDaLista[] = [...porDia.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dia, lista]) => {
      const ordenadas = [...lista].sort(ordemNoDia);
      const comValor = ordenadas.map(valorDaConta).filter((v): v is number => v !== null);
      return {
        dia,
        contas: ordenadas,
        subtotal: comValor.reduce((s, v) => s + v, 0),
        semValor: ordenadas.length - comValor.length,
      };
    });

  const soma = (l: ContaParaLista[]) => l.reduce((s, c) => s + (valorDaConta(c) ?? 0), 0);
  const semValorEm = (l: ContaParaLista[]) => l.filter((c) => valorDaConta(c) === null).length;
  return {
    hoje,
    periodo,
    ate,
    atrasadas,
    dias,
    quantidade: entram.length,
    total: soma(entram),
    semValor: semValorEm(entram),
    atrasadasTotal: soma(atrasadas),
    atrasadasSemValor: semValorEm(atrasadas),
    previstas: entram.filter((c) => c.valor_real === null && c.valor_previsto !== null).length,
    salariosFora,
  };
}

// --- Palavras ----------------------------------------------------------------------

/** Dinheiro sempre com centavos (é o valor que se paga). O espaço especial do
 *  Intl (NBSP) vira espaço comum: é ele que o PDF mede como espaço. */
export const reais = (v: number): string => brl(v).replace(/[\u00a0\u202f]/g, ' ');

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

const data = (iso: string) => new Date(`${iso}T12:00:00`);

/** "Quarta-feira, 23 de setembro" — com o ano só quando não é o ano de hoje. */
export function diaPorExtensoCurto(iso: string, hoje: string): string {
  const d = data(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const nome = DIAS[d.getDay()]!;
  const ano = iso.slice(0, 4) === hoje.slice(0, 4) ? '' : ` de ${d.getFullYear()}`;
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)}, ${d.getDate()} de ${MESES[d.getMonth()]}${ano}`;
}

/** "10/09 (quinta-feira)" — a data em que a conta venceu, absoluta. */
function venceuEm(iso: string, hoje: string): string {
  const d = data(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dm = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  const ano = iso.slice(0, 4) === hoje.slice(0, 4) ? '' : `/${d.getFullYear()}`;
  return `${dm}${ano} (${DIAS[d.getDay()]})`;
}

/** "6 de outubro" (com o ano se não for o de hoje) — para o placar. */
function diaEMes(iso: string, hoje: string): string {
  const d = data(iso);
  const ano = iso.slice(0, 4) === hoje.slice(0, 4) ? '' : ` de ${d.getFullYear()}`;
  return `${d.getDate()} de ${MESES[d.getMonth()]}${ano}`;
}

const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/**
 * O texto do banco pronto para o papel: uma linha só, e cada caractere que a
 * fonte do PDF não tem vira '?' — nos DOIS formatos, para a imagem e o PDF
 * dizerem o mesmo e a falta se notar (o mesmo critério do paraWinAnsi).
 * NFC antes: acento digitado no iPhone pode vir decomposto (e + ´), e o ´
 * solto não existe no WinAnsi.
 */
export function limparTexto(texto: string): string {
  // O hífen invisível (U+00AD, comum em texto copiado de PDF) sai: o PDF o
  // desenharia como "-" e o canvas não desenharia nada.
  return Array.from(texto.normalize('NFC').replace(/\u00ad/g, '').replace(/\s+/g, ' ').trim(),
    (c) => (representavelEmWinAnsi(c) ? c : '?')).join('');
}

/** Nome do arquivo em ASCII, com o período: 'contas-a-pagar-22-09-a-06-10.pdf';
 *  nas fotos, '...-1-de-4.png'. */
export function nomeDoArquivo(
  lista: Pick<ListaDeContas, 'hoje' | 'ate'>,
  extensao: 'pdf' | 'png',
  pagina?: { numero: number; total: number },
): string {
  const periodo = lista.ate
    ? `${ddmm(lista.hoje).replace('/', '-')}-a-${ddmm(lista.ate).replace('/', '-')}`
    : `em-aberto-${ddmm(lista.hoje).replace('/', '-')}`;
  const sufixo = pagina ? `-${pagina.numero}-de-${pagina.total}` : '';
  return `contas-a-pagar-${periodo}${sufixo}.${extensao}`;
}

// --- As peças e a folha ---------------------------------------------------------

export type Peca =
  | { tipo: 'texto'; texto: string; x: number; y: number; tamanho: number; negrito: boolean;
      alinhamento: Alinhamento; cinza: number }
  | { tipo: 'linha'; x1: number; y1: number; x2: number; y2: number; espessura: number; cinza: number }
  | { tipo: 'retangulo'; x: number; y: number; largura: number; altura: number; cinza: number }
  | { tipo: 'marca'; x: number; y: number; largura: number; cinza: number };

export interface Folha {
  larguraMm: number;
  alturaMm: number;
  pecas: Peca[];
}

export interface Geometria {
  larguraMm: number;
  alturaMm: number;
  margemMm: number;
}

/**
 * A página tem a forma de uma TELA DE CELULAR, 4:5 — 102,4 × 128 mm, que a
 * 10 px/mm vira uma foto de 1024 × 1280. O lado maior fica no menor teto
 * relatado da foto "padrão" do WhatsApp (1280 px): ela chega sem ser
 * encolhida, e a letra de 12 pt aparece com uns 16 pt na tela do pai.
 */
export const GEOMETRIA_CELULAR: Geometria = { larguraMm: 102.4, alturaMm: 128, margemMm: 6 };

/** Acima disto, as fotos ficam desligadas e a lista vai só em PDF: o canvas
 *  do iPhone tem teto de memória, e um álbum de 15 fotos cansa quem lê. */
export const MAXIMO_DE_FOTOS = 8;

/** 1 pt em mm. */
const PT = 25.4 / 72;

/** Os corpos do papel, em pt. */
const CORPO = {
  cabecalho: 8,
  pagina: 7.5,
  titulo: 15,
  periodo: 9.5,
  placarRotulo: 7.5,
  placarValor: 22,
  placarQtd: 9,
  ressalva: 8.5,
  faixa: 9,
  dia: 10.5,
  conta: 12,
  detalhe: 8.5,
  fim: 9.5,
  rodape: 7,
} as const;

const PRETO = 0.0667;     // #111111, o preto da marca: 0,0667 × 255 = 17
const CINZA = 0.35;       // o texto secundário
const FIO = 0.75;         // o fio claro entre contas

/** Altura de uma linha de texto e onde fica a linha de base dentro dela. */
const alturaLinha = (pt: number) => pt * PT * 1.28;
const baseLinha = (pt: number) => pt * PT * 0.92;

/**
 * Quebra por palavra SEM transbordar: palavra maior que a coluna (um código
 * colado, um nome sem espaço) é partida em pedaços — nenhum caractere some,
 * e a descrição não invade a coluna do valor. Diferente do quebrarEmLinhas do
 * vale, onde o que se assina não pode ser partido.
 */
export function quebrarSemTransbordar(texto: string, largura: number, tamanho: number, negrito = false): string[] {
  // A medida EXATA: o iPhone troca "--" por "—" sozinho, e o travessão medido
  // como letra comum deixava a descrição encostar no valor (revisão de
  // 22/09/2026).
  const medir = (t: string) => larguraExata(t, tamanho, negrito);
  const saida: string[] = [];
  let atual = '';
  for (const palavra of texto.split(/\s+/).filter(Boolean)) {
    const tentativa = atual === '' ? palavra : `${atual} ${palavra}`;
    if (medir(tentativa) <= largura) { atual = tentativa; continue; }
    if (atual !== '') saida.push(atual);
    atual = '';
    // Palavra maior que a coluna: em pedaços, sem perder caractere.
    for (const c of palavra) {
      if (atual !== '' && medir(atual + c) > largura) {
        saida.push(atual);
        atual = c;
      } else atual += c;
    }
  }
  if (atual !== '') saida.push(atual);
  return saida;
}

/** A largura que o texto ocupa no papel (a medida exata). */
export const larguraNoPapel = larguraExata;

/** Um pedaço de conteúdo com altura conhecida, que a paginação posiciona. */
interface Bloco {
  tipo: 'faixa' | 'dia' | 'conta' | 'fim';
  altura: number;
  /** Desenha o bloco com o topo em `y`. */
  desenhar: (y: number) => Peca[];
  /** Título: nunca fica sozinho no pé da página — vai junto com o próximo. */
  gruda?: boolean;
  /** Título que se repete no alto da página seguinte quando a seção continua. */
  continuacao?: () => Bloco;
}

interface Contexto {
  g: Geometria;
  hoje: string;
  comCodigo: boolean;
  esquerda: number;
  direita: number;
  util: number;
}

function texto(t: string, x: number, y: number, tamanho: number,
  o: { negrito?: boolean; alinhamento?: Alinhamento; cinza?: number } = {}): Peca {
  return {
    tipo: 'texto', texto: t, x, y, tamanho,
    negrito: o.negrito ?? false, alinhamento: o.alinhamento ?? 'esquerda', cinza: o.cinza ?? PRETO,
  };
}

/** A faixa preta das atrasadas: "JÁ VENCERAM E NÃO FORAM PAGAS". */
function blocoFaixa(ctx: Contexto, lista: ListaDeContas, continua = false): Bloco {
  const titulo = continua ? 'JÁ VENCERAM (continuação)' : 'JÁ VENCERAM E NÃO FORAM PAGAS';
  const n = lista.atrasadas.length;
  // O total da faixa segue a regra do total do dia: só com 2 ou mais, e a
  // conta sem valor aparece como "+ N a definir", nunca some.
  const total = n > 1 && lista.atrasadasTotal > 0 && !continua
    ? reais(lista.atrasadasTotal) + (lista.atrasadasSemValor ? ` + ${lista.atrasadasSemValor} a definir` : '')
    : null;
  const dentro = ctx.util - 4;
  const aoLado = total === null
    || larguraNoPapel(titulo, CORPO.faixa, true) + larguraNoPapel(total, CORPO.faixa, true) + 3 <= dentro;
  const lf = alturaLinha(CORPO.faixa);
  const altura = 2.4 + lf * (total && !aoLado ? 2 : 1);
  return {
    tipo: 'faixa',
    altura: altura + 2,
    gruda: true,
    continuacao: () => blocoFaixa(ctx, lista, true),
    desenhar: (y) => {
      const base = y + 1.2 + baseLinha(CORPO.faixa);
      const p: Peca[] = [
        { tipo: 'retangulo', x: ctx.esquerda, y, largura: ctx.util, altura, cinza: PRETO },
        texto(titulo, ctx.esquerda + 2, base, CORPO.faixa, { negrito: true, cinza: 1 }),
      ];
      if (total) {
        p.push(texto(total, ctx.direita - 2, aoLado ? base : base + lf, CORPO.faixa,
          { negrito: true, cinza: 1, alinhamento: 'direita' }));
      }
      return p;
    },
  };
}

/** O título do dia, com o total do dia à direita quando o dia tem 2 ou mais. */
function blocoDia(ctx: Contexto, dia: DiaDaLista, continua = false): Bloco {
  const titulo = diaPorExtensoCurto(dia.dia, ctx.hoje) + (continua ? ' (continuação)' : '');
  const total = !continua && dia.contas.length > 1 && dia.subtotal > 0
    ? reais(dia.subtotal) + (dia.semValor ? ` + ${dia.semValor} a definir` : '')
    : null;
  const lt = alturaLinha(CORPO.dia);
  // O título quebra linha se precisar: "Segunda-feira, 15 de novembro de 2027
  // (continuação)" passa da largura da folha (revisão de 22/09/2026).
  const linhas = quebrarSemTransbordar(titulo, ctx.util, CORPO.dia, true);
  // O total cabe ao lado do título? Senão, desce para a linha de baixo, à
  // direita — nunca por cima do título.
  const aoLado = total === null || (linhas.length === 1
    && larguraNoPapel(titulo, CORPO.dia, true) + larguraNoPapel(total, CORPO.dia, true) + 3 <= ctx.util);
  const altura = 2.2 + linhas.length * lt + (aoLado ? 0 : alturaLinha(CORPO.detalhe)) + 1.2;
  return {
    tipo: 'dia',
    altura,
    gruda: true,
    continuacao: () => blocoDia(ctx, dia, true),
    desenhar: (y) => {
      const base = y + 2.2 + baseLinha(CORPO.dia);
      const p: Peca[] = linhas.map((l, i) => texto(l, ctx.esquerda, base + i * lt, CORPO.dia, { negrito: true }));
      if (total && aoLado) p.push(texto(total, ctx.direita, base, CORPO.dia, { negrito: true, alinhamento: 'direita' }));
      if (total && !aoLado) {
        p.push(texto(total, ctx.direita, y + 2.2 + linhas.length * lt + baseLinha(CORPO.detalhe), CORPO.detalhe,
          { negrito: true, alinhamento: 'direita' }));
      }
      const fio = y + altura - 0.6;
      p.push({ tipo: 'linha', x1: ctx.esquerda, y1: fio, x2: ctx.direita, y2: fio, espessura: 0.35, cinza: PRETO });
      return p;
    },
  };
}

/** Uma conta: a descrição à esquerda, o valor à direita, e a linha de baixo
 *  (em cinza) só quando há o que dizer. */
function blocoConta(ctx: Contexto, c: ContaParaLista, atrasada: boolean, ultimaDoGrupo: boolean): Bloco {
  const v = valorDaConta(c);
  const valor = v === null ? 'a definir' : reais(v);
  const negritoValor = v !== null;
  const larguraValor = larguraNoPapel(valor, CORPO.conta, negritoValor);
  const colunaDescricao = ctx.util - larguraValor - 3;
  const descricao = quebrarSemTransbordar(limparTexto(c.descricao), colunaDescricao, CORPO.conta);

  const detalhes: string[] = [];
  if (atrasada) detalhes.push(`venceu em ${venceuEm(c.vencimento, ctx.hoje)}`);
  if (v === null) detalhes.push('o valor ainda não chegou');
  else if (c.valor_real === null) detalhes.push('valor previsto');
  const detalhe = detalhes.length
    ? quebrarSemTransbordar(detalhes.join(' · '), ctx.util, CORPO.detalhe)
    : [];
  const codigo = ctx.comCodigo && c.codigo_barras
    ? quebrarSemTransbordar(`Código de barras: ${formatarLinha(c.codigo_barras)}`, ctx.util, CORPO.detalhe)
    : [];

  const lc = alturaLinha(CORPO.conta);
  const ld = alturaLinha(CORPO.detalhe);
  const altura = 1.4 + descricao.length * lc + (detalhe.length + codigo.length) * ld + 1.6;
  return {
    tipo: 'conta',
    altura,
    desenhar: (y) => {
      const p: Peca[] = [];
      let topo = y + 1.4;
      descricao.forEach((linha, i) => {
        p.push(texto(linha, ctx.esquerda, topo + baseLinha(CORPO.conta), CORPO.conta));
        if (i === 0) {
          p.push(texto(valor, ctx.direita, topo + baseLinha(CORPO.conta), CORPO.conta,
            { negrito: negritoValor, alinhamento: 'direita', cinza: negritoValor ? PRETO : CINZA }));
        }
        topo += lc;
      });
      for (const linha of detalhe) {
        p.push(texto(linha, ctx.esquerda, topo + baseLinha(CORPO.detalhe), CORPO.detalhe, { cinza: CINZA }));
        topo += ld;
      }
      for (const linha of codigo) {
        p.push(texto(linha, ctx.esquerda, topo + baseLinha(CORPO.detalhe), CORPO.detalhe, { cinza: 0.2 }));
        topo += ld;
      }
      if (!ultimaDoGrupo) {
        const fio = y + altura - 0.4;
        p.push({ tipo: 'linha', x1: ctx.esquerda, y1: fio, x2: ctx.direita, y2: fio, espessura: 0.15, cinza: FIO });
      }
      return p;
    },
  };
}

/** O fecho da última página: "Fim da lista: N contas." */
function blocoFim(ctx: Contexto, lista: ListaDeContas): Bloco {
  const nota = quebrarSemTransbordar(
    'Pedido de compra só vira conta quando a mercadoria chega à loja.', ctx.util, CORPO.rodape);
  const altura = 3 + alturaLinha(CORPO.fim) + nota.length * alturaLinha(CORPO.rodape) + 1;
  return {
    tipo: 'fim',
    altura,
    desenhar: (y) => {
      const base = y + 3 + baseLinha(CORPO.fim);
      const p: Peca[] = [
        { tipo: 'linha', x1: ctx.esquerda, y1: y + 1.2, x2: ctx.direita, y2: y + 1.2, espessura: 0.35, cinza: PRETO },
        texto(`Fim da lista: ${plural(lista.quantidade, 'conta', 'contas')}.`, ctx.esquerda, base, CORPO.fim, { negrito: true }),
      ];
      let topo = y + 3 + alturaLinha(CORPO.fim);
      for (const l of nota) {
        p.push(texto(l, ctx.esquerda, topo + baseLinha(CORPO.rodape), CORPO.rodape, { cinza: CINZA }));
        topo += alturaLinha(CORPO.rodape);
      }
      return p;
    },
  };
}

/** As frases do topo — cada uma só aparece se for verdade. */
export function ressalvas(lista: ListaDeContas): string[] {
  const r: string[] = [];
  const n = lista.atrasadas.length;
  const vencidasComValor = n - lista.atrasadasSemValor;
  // A contagem ao lado do dinheiro é a das contas QUE SOMAM aquele dinheiro;
  // a vencida sem valor é dita à parte (revisão de 22/09/2026: "R$ 412,30
  // (2 contas)" com uma delas sem valor lia-se errado).
  if (vencidasComValor > 0) {
    const semValor = lista.atrasadasSemValor
      ? `; e ${plural(lista.atrasadasSemValor, 'vencida ainda está sem valor', 'vencidas ainda estão sem valor')}`
      : '';
    r.push(`Deste total, ${reais(lista.atrasadasTotal)} já venceu e não foi pago `
      + `(${plural(vencidasComValor, 'conta', 'contas')})${semValor}.`);
  } else if (n > 0) {
    r.push(`${plural(n, 'conta já venceu e não foi paga', 'contas já venceram e não foram pagas')}, ainda sem valor.`);
  }
  const comValor = lista.quantidade - lista.semValor;
  if (lista.semValor > 0 && comValor > 0) {
    // "Destas", e não "mais": a conta sem valor já está no número de contas do
    // placar; "mais" a contava duas vezes.
    r.push(`Destas, ${plural(lista.semValor, 'conta ainda está sem valor e fica', 'contas ainda estão sem valor e ficam')}`
      + ' fora do total.');
  }
  if (lista.previstas > 0) {
    r.push(lista.previstas === comValor
      ? 'Todo o total é valor previsto: pode mudar quando as contas chegarem.'
      : 'Parte do total é valor previsto: pode mudar quando a conta chegar.');
  }
  if (lista.salariosFora > 0) r.push('Não inclui salários e pró-labore.');
  return r;
}

/** O cabeçalho da 1ª página: marca, título, período, placar e ressalvas.
 *  Devolve as peças e onde o conteúdo começa. */
function cabecalhoPrimeira(ctx: Contexto, lista: ListaDeContas, pagina: string): { pecas: Peca[]; fim: number } {
  const { esquerda, direita, util } = ctx;
  const p: Peca[] = [];
  const topo = ctx.g.margemMm;
  const LADO = 8;
  p.push({ tipo: 'marca', x: esquerda, y: topo, largura: LADO, cinza: PRETO });
  const baseMarca = topo + 4;
  p.push(texto(negocio.nome, esquerda + LADO + 2.2, baseMarca, CORPO.cabecalho, { negrito: true }));
  p.push(texto(pagina, direita, baseMarca, CORPO.pagina, { alinhamento: 'direita', cinza: CINZA }));

  let y = topo + LADO * (502 / 738) + 3.2;
  p.push(texto('CONTAS A PAGAR', esquerda, y + baseLinha(CORPO.titulo), CORPO.titulo, { negrito: true }));
  y += alturaLinha(CORPO.titulo);
  const periodo = lista.ate
    ? `de ${diaPorExtensoCurto(lista.hoje, lista.hoje).toLowerCase()} a ${diaPorExtensoCurto(lista.ate, lista.hoje).toLowerCase()}`
    : 'todas as contas em aberto';
  for (const l of quebrarSemTransbordar(periodo, util, CORPO.periodo)) {
    p.push(texto(l, esquerda, y + baseLinha(CORPO.periodo), CORPO.periodo, { cinza: CINZA }));
    y += alturaLinha(CORPO.periodo);
  }
  y += 2.4;

  // O PLACAR: um número só, grande — se ele ler só isto, já valeu.
  const PAD = 3.2;
  const linhasPlacar: Array<{ t: string; pt: number; negrito: boolean }> = [];
  const rotulo = lista.ate ? `TOTAL PARA PAGAR ATÉ ${diaEMes(lista.ate, lista.hoje).toUpperCase()}` : 'TOTAL EM ABERTO';
  if (lista.quantidade === 0) {
    const frase = lista.ate
      ? `Nenhuma conta para pagar até ${diaEMes(lista.ate, lista.hoje)}.`
      : 'Nenhuma conta em aberto.';
    for (const l of quebrarSemTransbordar(frase, util - 2 * PAD, CORPO.conta, true)) {
      linhasPlacar.push({ t: l, pt: CORPO.conta, negrito: true });
    }
  } else {
    linhasPlacar.push({ t: rotulo, pt: CORPO.placarRotulo, negrito: true });
    if (lista.semValor === lista.quantidade) {
      const frase = `${plural(lista.quantidade, 'conta, ainda sem valor', 'contas, todas ainda sem valor')}`;
      for (const l of quebrarSemTransbordar(frase, util - 2 * PAD, CORPO.conta, true)) {
        linhasPlacar.push({ t: l, pt: CORPO.conta, negrito: true });
      }
    } else {
      linhasPlacar.push({ t: reais(lista.total), pt: CORPO.placarValor, negrito: true });
      linhasPlacar.push({ t: plural(lista.quantidade, 'conta', 'contas'), pt: CORPO.placarQtd, negrito: false });
    }
  }
  const alturaPlacar = PAD + linhasPlacar.reduce((s, l) => s + alturaLinha(l.pt), 0) + PAD - 1;
  p.push({ tipo: 'retangulo', x: esquerda, y, largura: util, altura: alturaPlacar, cinza: PRETO });
  let yp = y + PAD;
  for (const l of linhasPlacar) {
    p.push(texto(l.t, esquerda + PAD, yp + baseLinha(l.pt), l.pt, { negrito: l.negrito, cinza: 1 }));
    yp += alturaLinha(l.pt);
  }
  y += alturaPlacar + 2.4;

  for (const frase of ressalvas(lista)) {
    const linhas = quebrarSemTransbordar(frase, util - 3, CORPO.ressalva);
    linhas.forEach((l, i) => {
      if (i === 0) p.push(texto('•', esquerda, y + baseLinha(CORPO.ressalva), CORPO.ressalva));
      p.push(texto(l, esquerda + 3, y + baseLinha(CORPO.ressalva), CORPO.ressalva));
      y += alturaLinha(CORPO.ressalva);
    });
  }
  return { pecas: p, fim: y + 1.5 };
}

/** O cabeçalho das páginas seguintes: marca pequena, o que é e a página. */
function cabecalhoSeguinte(ctx: Contexto, lista: ListaDeContas, pagina: string): { pecas: Peca[]; fim: number } {
  const topo = ctx.g.margemMm;
  const LADO = 6;
  const base = topo + 3.1;
  const oQue = lista.ate
    ? `Contas a pagar · ${ddmm(lista.hoje)} a ${ddmm(lista.ate)}`
    : `Contas a pagar · em aberto em ${ddmm(lista.hoje)}`;
  const pecas: Peca[] = [
    { tipo: 'marca', x: ctx.esquerda, y: topo, largura: LADO, cinza: PRETO },
    texto(oQue, ctx.esquerda + LADO + 2, base, CORPO.cabecalho, { negrito: true }),
    texto(pagina, ctx.direita, base, CORPO.pagina, { alinhamento: 'direita', cinza: CINZA }),
  ];
  const fim = topo + LADO * (502 / 738) + 2;
  pecas.push({ tipo: 'linha', x1: ctx.esquerda, y1: fim, x2: ctx.direita, y2: fim, espessura: 0.2, cinza: FIO });
  return { pecas, fim: fim + 1 };
}

/** O rodapé de toda página: quando a lista foi feita. */
function rodape(ctx: Contexto, agora: Date): { linhas: string[]; altura: number } {
  const dois = (n: number) => String(n).padStart(2, '0');
  const quando = `${dois(agora.getDate())}/${dois(agora.getMonth() + 1)}/${agora.getFullYear()}`
    + ` às ${dois(agora.getHours())}:${dois(agora.getMinutes())}`;
  const linhas = quebrarSemTransbordar(
    `Lista feita em ${quando}. O que foi pago ou lançado depois disso não aparece.`, ctx.util, CORPO.rodape);
  return { linhas, altura: linhas.length * alturaLinha(CORPO.rodape) + 1.5 };
}

/**
 * A lista vira FOLHAS: cada uma com as suas peças, já com "página X de N".
 *
 * Duas passadas: a primeira arruma os blocos nas páginas (conta nunca partida
 * entre duas; título nunca sozinho no pé; o dia que continua repete o título
 * com "(continuação)"), e só então, sabendo N, a segunda desenha.
 */
export function paginar(
  lista: ListaDeContas,
  g: Geometria,
  opcoes: { agora: Date; comCodigo: boolean },
): Folha[] {
  const ctx: Contexto = {
    g, hoje: lista.hoje, comCodigo: opcoes.comCodigo,
    esquerda: g.margemMm, direita: g.larguraMm - g.margemMm, util: g.larguraMm - 2 * g.margemMm,
  };

  const blocos: Bloco[] = [];
  if (lista.atrasadas.length) {
    blocos.push(blocoFaixa(ctx, lista));
    lista.atrasadas.forEach((c, i) => blocos.push(blocoConta(ctx, c, true, i === lista.atrasadas.length - 1)));
  }
  for (const dia of lista.dias) {
    blocos.push(blocoDia(ctx, dia));
    dia.contas.forEach((c, i) => blocos.push(blocoConta(ctx, c, false, i === dia.contas.length - 1)));
  }
  if (lista.quantidade > 0) blocos.push(blocoFim(ctx, lista));

  const pe = rodape(ctx, opcoes.agora);
  const limite = g.alturaMm - g.margemMm - pe.altura;
  // O cabeçalho não muda de altura com o número da página: dá para medir já.
  const inicioPrimeira = cabecalhoPrimeira(ctx, lista, 'página 1 de 1').fim;
  const inicioSeguinte = cabecalhoSeguinte(ctx, lista, 'página 1 de 1').fim;

  // 1ª passada: onde cada bloco cai.
  const paginas: Array<Array<{ bloco: Bloco; y: number }>> = [[]];
  let y = inicioPrimeira;
  let secao: Bloco | null = null;
  blocos.forEach((b, i) => {
    const proximo = blocos[i + 1];
    const precisa = b.altura + (b.gruda && proximo ? proximo.altura : 0);
    const atual = paginas[paginas.length - 1]!;
    if (y + precisa > limite && atual.length > 0) {
      paginas.push([]);
      y = inicioSeguinte;
      // A conta que abre a página nova leva o título da seção junto.
      if (b.tipo === 'conta' && secao?.continuacao) {
        const cont = secao.continuacao();
        paginas[paginas.length - 1]!.push({ bloco: cont, y });
        y += cont.altura;
      }
    } else if (y + precisa > limite && atual.length === 0 && paginas.length === 1) {
      // O cabeçalho da 1ª página comeu o espaço todo (muita ressalva numa
      // folha pequena): o conteúdo começa na 2ª.
      paginas.push([]);
      y = inicioSeguinte;
    }
    paginas[paginas.length - 1]!.push({ bloco: b, y });
    y += b.altura;
    if (b.tipo === 'faixa' || b.tipo === 'dia') secao = b;
  });

  // 2ª passada: desenhar, agora sabendo quantas são.
  const total = paginas.length;
  return paginas.map((colocados, i) => {
    const rotulo = `página ${i + 1} de ${total}`;
    const cab = i === 0 ? cabecalhoPrimeira(ctx, lista, rotulo) : cabecalhoSeguinte(ctx, lista, rotulo);
    const pecas: Peca[] = [...cab.pecas];
    for (const { bloco, y: yb } of colocados) pecas.push(...bloco.desenhar(yb));
    let yr = g.alturaMm - g.margemMm - pe.altura + 1.5;
    for (const l of pe.linhas) {
      pecas.push(texto(l, ctx.esquerda, yr + baseLinha(CORPO.rodape), CORPO.rodape, { cinza: CINZA }));
      yr += alturaLinha(CORPO.rodape);
    }
    return { larguraMm: g.larguraMm, alturaMm: g.alturaMm, pecas };
  });
}
